export interface RoleDefinition {
  rolename: string
  roleid: string
  permission: string
  roleType: string
  customerType: string
  possibleRoles: string
  isUnfilteredEnabled?: boolean
  isTankEnabled?: boolean
}

/**
 * Static mapping of Gigya roleId → full role definition.
 * Used as fallback when GET_USER_INFO API is unavailable.
 * Source: cross-reference of GET_USER_INFO response (rpDetails) and Gigya getUserInfo (roles[]).
 * Update this map when new roles are introduced in the platform.
 *
 * Gigya assigns DIFFERENT roleIds per environment (UAT vs PROD) for the same logical role.
 * Both sets coexist here: lookup is by roleId so there is no collision.
 * When updating a permission string, remember to update BOTH the UAT and PROD entries.
 */
export const ROLE_DEFINITIONS: Record<string, RoleDefinition> = {
  // === UAT ===
  '90e0f8c4-a996-4a0d-bdfe-50647f318a7d': {
    rolename: 'POS owner',
    roleid: '90e0f8c4-a996-4a0d-bdfe-50647f318a7d',
    permission: 'Home Page,My Profile,Contracts,Reporting,My Orders,My Documents,Marketing Materials,News & Articles,Support,Academy,Access Management,Catalogue',
    roleType: 'Basic',
    customerType: 'POS',
    possibleRoles: 'POS Delegated owner,POS Associate,POS Shift leader,POS Accountant,POS Common user'
  },
  '3c6f26b8-bf43-4922-a249-39d2abb84cb7': {
    rolename: 'POS Delegated owner',
    roleid: '3c6f26b8-bf43-4922-a249-39d2abb84cb7',
    permission: 'Home Page,My Profile,Contracts,Reporting,My Orders,My Documents,Marketing Materials,News & Articles,Support,Academy,Access Management,Catalogue',
    roleType: 'Basic',
    customerType: 'POS',
    possibleRoles: 'POS Delegated owner,POS Associate,POS Shift leader,POS Accountant,POS Common user'
  },
  '9a26052f-3412-4698-997a-efc701c3ef3b': {
    rolename: 'POS POSM order',
    roleid: '9a26052f-3412-4698-997a-efc701c3ef3b',
    permission: 'POSM Orders',
    roleType: 'Extension',
    customerType: 'POS',
    possibleRoles: ''
  },
  'f7c69acd-2ab4-4c40-b8a1-f409b72be9c9': {
    rolename: 'POS Product Recommendation',
    roleid: 'f7c69acd-2ab4-4c40-b8a1-f409b72be9c9',
    permission: 'Product Recommendation',
    roleType: 'Extension',
    customerType: 'POS',
    possibleRoles: ''
  },
  '2d90f4d8-6dda-4700-83aa-4c8edc82c6fa': {
    rolename: 'POS Suggested Cart',
    roleid: '2d90f4d8-6dda-4700-83aa-4c8edc82c6fa',
    permission: 'Suggested Cart',
    roleType: 'Extension',
    customerType: 'POS',
    possibleRoles: ''
  },
  'a2b25be9-3d12-444f-ab66-1324d3ff7a4f': {
    rolename: 'POS Unfiltered Beer',
    roleid: 'a2b25be9-3d12-444f-ab66-1324d3ff7a4f',
    permission: 'Unfiltered Beer',
    roleType: 'Extension',
    customerType: 'POS',
    possibleRoles: ''
  },
  '757fad7b-457d-46cd-b6cd-067d7ad3f0eb': {
    rolename: 'WHS owner',
    roleid: '757fad7b-457d-46cd-b6cd-067d7ad3f0eb',
    permission: 'Home Page,My Profile,Contracts,Reporting,My Documents,My Orders,Catalogue,Marketing Materials,News & Articles,Support,Academy,Loyalty,Access Management',
    roleType: 'Basic',
    customerType: 'WHSL',
    possibleRoles: 'WHS Delegated owner,WHS Warehouse man,WHS Accountant,WHS Common user'
  },
  '2ceff1ac-d5ce-4f08-93bd-6be06f8bf698': {
    rolename: 'WHS Delegated owner',
    roleid: '2ceff1ac-d5ce-4f08-93bd-6be06f8bf698',
    permission: 'Home Page,My Profile,Contracts,Reporting,My Orders,My Documents,Catalogue,Marketing Materials,News & Articles,Support,Academy,Loyalty,Access Management',
    roleType: 'Basic',
    customerType: 'WHSL',
    possibleRoles: 'WHS Delegated owner,WHS Warehouse man,WHS Accountant,WHS Common user'
  },
  'f438c860-a2a4-4468-b876-5fd3987bc312': {
    rolename: 'WHS Product Recommendation',
    roleid: 'f438c860-a2a4-4468-b876-5fd3987bc312',
    permission: 'Product Recommendation',
    roleType: 'Extension',
    customerType: 'WHSL',
    possibleRoles: ''
  },
  '9926e264-4b21-4dc8-bd80-6d0a79cbb271': {
    rolename: 'WHS Suggested Cart',
    roleid: '9926e264-4b21-4dc8-bd80-6d0a79cbb271',
    permission: 'Suggested Cart',
    roleType: 'Extension',
    customerType: 'WHSL',
    possibleRoles: ''
  },
  'f698d18e-0c9a-4535-bc26-c13b5cdfb62d': {
    rolename: 'WHS Unfiltered Beer',
    roleid: 'f698d18e-0c9a-4535-bc26-c13b5cdfb62d',
    permission: 'Unfiltered Beer',
    roleType: 'Extension',
    customerType: 'WHSL',
    possibleRoles: ''
  },
  // === PROD ===
  'f86f1c9e-e9c9-4da3-9011-45c9550b85cf': {
    rolename: 'POS Delegated owner',
    roleid: 'f86f1c9e-e9c9-4da3-9011-45c9550b85cf',
    permission: 'Home Page,My Profile,Contracts,Reporting,My Documents,My Orders,Marketing Materials,News & Articles,Support,Academy,Access Management,Catalogue',
    roleType: 'Basic',
    customerType: 'POS',
    possibleRoles: 'POS Delegated owner,POS Associate,POS Shift leader,POS Accountant,POS Common user'
  },
  'ec12f394-6d2f-45d3-849c-d553343c15f4': {
    rolename: 'POS Product Recommendation',
    roleid: 'ec12f394-6d2f-45d3-849c-d553343c15f4',
    permission: 'Product Recommendation',
    roleType: 'Extension',
    customerType: 'POS',
    possibleRoles: ''
  },
  '21b89e80-408e-402f-bc02-8e3fc805f28e': {
    rolename: 'POS Suggested Cart',
    roleid: '21b89e80-408e-402f-bc02-8e3fc805f28e',
    permission: 'Suggested Cart',
    roleType: 'Extension',
    customerType: 'POS',
    possibleRoles: ''
  },
  '24148960-b40c-48a7-9c6b-d82ffd3d13a6': {
    rolename: 'WHS Delegated owner',
    roleid: '24148960-b40c-48a7-9c6b-d82ffd3d13a6',
    permission: 'Home Page,My Profile,Contracts,Reporting,My Documents,My Orders,Catalogue,Marketing Materials,News & Articles,Support,Academy,Loyalty,Access Management',
    roleType: 'Basic',
    customerType: 'WHSL',
    possibleRoles: 'WHS Delegated owner,WHS Warehouse man,WHS Accountant,WHS Common user'
  },
  '1876b09b-35b0-4bbb-b514-120ff39fdcea': {
    rolename: 'WHS Product Recommendation',
    roleid: '1876b09b-35b0-4bbb-b514-120ff39fdcea',
    permission: 'Product Recommendation',
    roleType: 'Extension',
    customerType: 'WHSL',
    possibleRoles: ''
  }
}
